import type { ColumnDef } from '@tanstack/react-table';
import { DEAD_PART_REASON_CODES, hasCdkDesc } from '../../api/checks';
import type { Check } from '../../api/checks';
import { CheckStatusBadge, ReasonBadge } from '../badges';
import { Badge } from '../ui/badge';
import { NsIdLink } from './columns';
import { fmtDate, fmtDateTime, fmtPct, fmtPrice, fmtVendor } from '../../utils/format';

/**
 * Column definitions shared by Review / Queue / Processed (BOT-392).
 *
 * The review's core complaint was that the same value looked different on every
 * screen. Defining each column once — rather than three near-copies — is what
 * actually holds that line as the pages keep changing.
 */

/** SKU + description subline. Always the first data column. */
export const skuColumn: ColumnDef<Check> = {
  id: 'sku',
  size: 230,
  enableHiding: false,
  meta: { label: 'SKU' },
  header: 'SKU',
  cell: ({ row }) => (
    <>
      <div className="whitespace-nowrap font-mono text-[12.5px] font-medium">{row.original.sku ?? '—'}</div>
      {/* CDK vendors only. `cdk_desc` is raw_payload.description for every
          vendor, so ungated this line showed Freightliner's Prop-65 warning
          and CAT's placeholder text under the SKU. Other vendors' description
          belongs in the expanded row, not in the scan column.
          decision: cdkdesc — see decisions/cdkdesc.md */}
      {hasCdkDesc(row.original) && (
        <div className="max-w-[240px] truncate text-xs text-muted-foreground" title={row.original.cdk_desc!}>
          {row.original.cdk_desc}
        </div>
      )}
      {/* BOT-384: live Paccar Parts Online status (nightly hf-paccar-portal-sync).
          The portal's own RED code (RSV/CNR/NSP/...) plus the ADDITIONAL
          DESCRIPTION text the client reads there ("LIMITED TO 1PC PER DEALER
          PER DAY"). Only Review selects the portal fields — undefined, so
          hidden, on Queue / Processed. */}
      {row.original.reason_code?.startsWith('CDK_') &&
        (row.original.portal_dis_code || row.original.portal_text) && (
          <Badge
            variant="info"
            className="mt-0.5 max-w-[240px]"
            title={`Paccar Parts Online${
              row.original.portal_checked_at ? `, checked ${fmtDate(row.original.portal_checked_at)}` : ''
            }`}
          >
            <span className="truncate">
              Paccar: {[row.original.portal_dis_code, row.original.portal_text].filter(Boolean).join(' — ')}
            </span>
          </Badge>
        )}
    </>
  ),
};

export const vendorColumn: ColumnDef<Check> = {
  id: 'vendor',
  meta: { label: 'Vendor' },
  header: 'Vendor',
  cell: ({ row }) => (
    <span className="whitespace-nowrap text-zinc-700">{fmtVendor(row.original.vendor_name)}</span>
  ),
};

export const nsIdColumn: ColumnDef<Check> = {
  id: 'ns',
  meta: { label: 'NS ID' },
  header: 'NS ID',
  cell: ({ row }) => <NsIdLink id={row.original.ns_item_id} />,
};

export const reasonColumn: ColumnDef<Check> = {
  id: 'reason',
  meta: { label: 'Reason' },
  header: 'Reason',
  cell: ({ row }) => (
    <div className="flex flex-col items-start gap-1">
      <ReasonBadge code={row.original.reason_code} />
      {row.original.secondary_reason_code && <ReasonBadge code={row.original.secondary_reason_code} />}
      {row.original.cdk_pnc && row.original.reason_code?.startsWith('CDK_') && (
        <span className="font-mono text-[10px] text-muted-foreground" title="CDK replacement part number (PNC)">
          → {row.original.cdk_pnc}
        </span>
      )}
      {/* Replacement read live from the portal's Exception popup — covers MSS
          chains and SSD rows whose CDK file row had no PNC. Hidden when the
          file already names one (cdk_pnc). Sky, not muted, to mark it as
          portal-sourced rather than file-sourced. */}
      {row.original.portal_supersedure &&
        !row.original.cdk_pnc &&
        row.original.reason_code?.startsWith('CDK_') && (
          <span className="font-mono text-[10px] text-sky-700" title="Replacement per Paccar Parts Online (Exception)">
            → {row.original.portal_supersedure}
          </span>
        )}
      {/* BOT-377: replacement named by the vendor portal page itself (fref
          "Replaced by: X"). NOT gated on the reason code — IS_DISCONTINUED
          outranks IS_SUPERSEDED, so a part that is both would otherwise hide
          the successor the operator is supposed to buy instead. */}
      {row.original.vendor_supersedure && (
        <span className="font-mono text-[10px] text-sky-700" title="Replacement per vendor portal (Replaced by)">
          → {row.original.vendor_supersedure}
        </span>
      )}
      {/* Reman counterpart — a different relation, so it is labelled instead of
          arrowed, and only on a dead part: a live priced part can have a reman
          twin, and showing it on a plain price move would just be noise. */}
      {row.original.vendor_reman_code && DEAD_PART_REASON_CODES.has(row.original.reason_code ?? '') && (
        <span className="font-mono text-[10px] text-muted-foreground" title="Remanufactured counterpart per vendor portal (Reman)">
          reman {row.original.vendor_reman_code}
        </span>
      )}
    </div>
  ),
};

/** NS purchase cost + the NS-side core charge underneath. */
export const purchaseCostColumn: ColumnDef<Check> = {
  id: 'purchase',
  meta: { label: 'Purchase cost', align: 'right' },
  header: 'Purchase cost',
  cell: ({ row }) => (
    <div className="whitespace-nowrap">
      <div className="font-medium">{fmtPrice(row.original.our_price)}</div>
      {row.original.ns_core_charge != null && (
        <div className="text-[11.5px] text-zinc-400">{fmtPrice(row.original.ns_core_charge)} core</div>
      )}
    </div>
  ),
};

export const vendorCostColumn: ColumnDef<Check> = {
  id: 'vendorCost',
  meta: { label: 'Vendor cost', align: 'right' },
  header: 'Vendor cost',
  cell: ({ row }) => (
    <div className="whitespace-nowrap">
      <div className="font-medium">{fmtPrice(row.original.vendor_price)}</div>
      {row.original.core_charge && row.original.core_charge_amount != null && (
        <div className="text-[11.5px] text-zinc-400">{fmtPrice(row.original.core_charge_amount)} core</div>
      )}
    </div>
  ),
};

export const specialPriceColumn: ColumnDef<Check> = {
  id: 'special',
  meta: { label: 'Special price', align: 'right' },
  header: 'Special price',
  cell: ({ row }) =>
    row.original.special_price != null ? (
      <span className="whitespace-nowrap">{fmtPrice(row.original.special_price)}</span>
    ) : (
      <span className="text-zinc-300">—</span>
    ),
};

/** Direction, not magnitude: up costs us money, down saves it. */
export const pctDiffColumn: ColumnDef<Check> = {
  id: 'diff',
  meta: { label: '% diff', align: 'right' },
  header: '% diff',
  cell: ({ row }) => {
    const v = row.original.pct_diff;
    return (
      <span className={v > 0 ? 'text-amber-700' : v < 0 ? 'text-green-700' : 'text-muted-foreground'}>
        {fmtPct(v)}
      </span>
    );
  },
};

export const checkStatusColumn: ColumnDef<Check> = {
  id: 'status',
  meta: { label: 'Status' },
  header: 'Status',
  cell: ({ row }) => <CheckStatusBadge status={row.original.status} />,
};

export const reviewerColumn: ColumnDef<Check> = {
  id: 'reviewer',
  meta: { label: 'Reviewed by' },
  header: 'Reviewed by',
  cell: ({ row }) => (
    <span className="whitespace-nowrap text-muted-foreground">{row.original.reviewer_email ?? '—'}</span>
  ),
};

/** Scrape date, falling back to the check's creation time on legacy rows. */
export const priceDateColumn: ColumnDef<Check> = {
  id: 'date',
  meta: { label: 'Price date' },
  header: 'Price date',
  cell: ({ row }) => {
    const { price_date, created_at } = row.original;
    if (price_date) {
      return <span className="whitespace-nowrap text-muted-foreground">{fmtDate(price_date)}</span>;
    }
    return (
      <span
        className="whitespace-nowrap text-muted-foreground"
        title="Scrape date unavailable — showing the check creation date"
      >
        {fmtDate(created_at)}
        {created_at ? '*' : ''}
      </span>
    );
  },
};

/** Timestamps carry the time as well as the day — several checks can land for
 *  the same item in one run, and the day alone does not distinguish them. */
export function timestampColumn(id: string, label: string, pick: (c: Check) => string | null): ColumnDef<Check> {
  return {
    id,
    meta: { label },
    header: label,
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">{fmtDateTime(pick(row.original))}</span>
    ),
  };
}

/** NS stock available to sell. Highlighted on CDK-coded rows that still have
 *  stock — the operator rule is "never disable while we can still sell units". */
export const availableColumn: ColumnDef<Check> = {
  id: 'available',
  meta: { label: 'Available', align: 'right' },
  header: 'Available',
  cell: ({ row }) => {
    const v = row.original.ns_available;
    if (v == null) return <span className="text-xs text-zinc-300">—</span>;
    const flagged = v > 0 && row.original.reason_code?.startsWith('CDK_');
    return (
      <span className={flagged ? 'font-medium text-amber-700' : v > 0 ? 'text-foreground' : 'text-zinc-400'}>
        {v}
      </span>
    );
  },
};

export const manufacturerColumn: ColumnDef<Check> = {
  id: 'manufacturer',
  meta: { label: 'Manufacturer' },
  header: 'Manufacturer',
  cell: ({ row }) => (
    <span className="whitespace-nowrap text-zinc-700">{row.original.manufacturer ?? '—'}</span>
  ),
};
